// src/components/settings/QuoteFollowUpSettings.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { MessageSquare, Bell, Mail, AlertTriangle, CheckCircle } from 'lucide-react';
import { resolveDocTerminology } from '../../utils/quoteSchema';

export default function QuoteFollowUpSettings({ currentUserProfile }) {
    const orgId = currentUserProfile?.organizationId;

    const [enabled, setEnabled]           = useState(false);
    const [reminderDays, setReminderDays] = useState([3, 7, 14]);
    // qnotif-6d2f8b47 (Seam 11c) — daily digest. ON unless explicitly false.
    const [digestEnabled, setDigestEnabled] = useState(true);
    const [isSaving, setIsSaving]         = useState(false);
    const [saved, setSaved]               = useState(false);
    const [error, setError]               = useState('');
    const [loading, setLoading]           = useState(true);
    const [docWord, setDocWord]           = useState('Quote'); // r1-p2-docterm-qfollowsettings-d5b8e1a4 — org-default term for these labels (aggregate surface)

    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(doc(db, 'organizations', orgId), (snap) => {
            if (snap.exists()) {
                const s = snap.data().quoteFollowUpSettings || {};
                setEnabled(s.enabled ?? false);
                setReminderDays(s.reminderDays ?? [3, 7, 14]);
                setDigestEnabled(s.digestEnabled !== false);
                setDocWord(resolveDocTerminology(snap.data().quoteTerminologyDefault).word);
            }
            setLoading(false);
        });
    }, [orgId]);

    const handleDayChange = (index, value) => {
        const days = [...reminderDays];
        days[index] = parseInt(value) || 1;
        setReminderDays(days);
    };

    const handleSave = async () => {
        setError('');
        // Validate: each reminder must be > the previous
        for (let i = 1; i < reminderDays.length; i++) {
            if (reminderDays[i] <= reminderDays[i - 1]) {
                setError(`Reminder ${i + 1} must be more days than Reminder ${i}.`);
                return;
            }
        }
        if (reminderDays.some(d => d < 1)) {
            setError('All reminder days must be at least 1.');
            return;
        }
        setIsSaving(true);
        try {
            await updateDoc(doc(db, 'organizations', orgId), {
                'quoteFollowUpSettings.enabled':     enabled,
                'quoteFollowUpSettings.reminderDays': reminderDays,
                'quoteFollowUpSettings.digestEnabled': digestEnabled,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setError('Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <div className="p-6 text-sm text-gray-400">Loading…</div>;

    const ordinals = ['1st', '2nd', '3rd'];

    return (
        <div className="p-6 space-y-6 max-w-2xl">
            <div>
                <h2 className="text-lg font-bold text-gray-800">{docWord} Follow-up Reminders</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Automatically remind your team to follow up when a {docWord.toLowerCase()} hasn't been responded to.
                    Reminders stop as soon as the customer approves or declines.
                </p>
            </div>

            {/* Enable toggle */}
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
                <div>
                    <p className="text-sm font-semibold text-gray-800">Enable {docWord.toLowerCase()} follow-up reminders</p>
                    <p className="text-xs text-gray-500 mt-0.5">Send up to 3 reminders per unanswered {docWord.toLowerCase()}</p>
                </div>
                <button
                    type="button"
                    onClick={() => setEnabled(p => !p)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
            </div>

            {/* Reminder schedule */}
            {enabled && (
                <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-700">Reminder schedule</p>
                    <p className="text-xs text-gray-400">
                        Days after the {docWord.toLowerCase()} is sent. Each reminder fires once — your team gets a bell notification and an email.
                    </p>
                    {reminderDays.map((days, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                {i + 1}
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-700">{ordinals[i]} reminder</p>
                                <p className="text-xs text-gray-400">Fires if no response after this many days</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="1"
                                    max="90"
                                    value={days}
                                    onChange={e => handleDayChange(i, e.target.value)}
                                    className="w-16 p-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                                <span className="text-sm text-gray-500">days</span>
                            </div>
                        </div>
                    ))}

                    {/* What happens */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
                        <p className="text-xs font-semibold text-blue-800">What each reminder does:</p>
                        <div className="flex items-start gap-2">
                            <Bell size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-700">Bell notification in-app to all Owners, Admins, and Managers</p>
                        </div>
                        <div className="flex items-start gap-2">
                            <Mail size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-700">Email to your org's Owner/Admin with a direct link to the {docWord.toLowerCase()}</p>
                        </div>
                        <div className="flex items-start gap-2">
                            <MessageSquare size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-700">Reminders stop automatically when the customer approves or declines</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Daily digest — qnotif-6d2f8b47 (Seam 11c). Independent of the reminders above. */}
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
                <div>
                    <p className="text-sm font-semibold text-gray-800">Daily {docWord.toLowerCase()} summary</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        One notification each morning at 9:00 with only what's new: follow-ups due today, {docWord.toLowerCase()}s about to expire,
                        newly flagged {docWord.toLowerCase()}s and snoozes that ended. Each salesperson gets their own; {docWord.toLowerCase()}s with
                        no salesperson go to Owners and Admins. Nothing new, no notification.
                    </p>
                </div>
                <button
                    type="button"
                    aria-label={`Daily ${docWord.toLowerCase()} summary`}
                    onClick={() => setDigestEnabled(p => !p)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${digestEnabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${digestEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
            </div>

            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={14} /> {error}
                </div>
            )}

            <div className="flex items-center gap-3">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                    {isSaving ? 'Saving…' : 'Save'}
                </button>
                {saved && (
                    <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle size={14} /> Saved
                    </span>
                )}
            </div>
        </div>
    );
}